import { Slot } from '@radix-ui/react-slot';
import { type Typography } from '@smart/web-foundations';
import classNames from 'classnames';
import React from 'react';
import type { ExtendWithCommonProps } from '../../types';
import { Layout, type LayoutProps } from '../layout/layout';
import scss from './text.module.scss';

export type TextProps = ExtendWithCommonProps<
  Omit<
    LayoutProps,
    | 'backgroundColor'
    | 'borderColor'
    | 'borderWidth'
    | 'borderRadius'
    | 'boxShadow'
  >,
  {
    id?: string;
    /** Text variant. */
    variant?: Typography;
    /** Component's overflow wrap. */
    overflowWrap?: 'normal' | 'anywhere' | 'break-word';
    /** HTML tag to be used. */
    as?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /** If true, skips rendering tag and passes props to child. */
    asChild?: boolean;
    /** Component's children. */
    children?: React.ReactNode;
  }
>;

/**
 * Renders text with different typography variants.
 * Use the `variant` prop to set the typography variant.
 *
 * @link https://prod.web-components.srv.smart.com/?path=/docs/components-text--docs
 * @link https://prod.web-components.srv.smart.com/?path=/docs/foundation-typography--docs
 */
export const Text = React.forwardRef<React.ElementRef<'p'>, TextProps>(
  (
    {
      children,
      className,
      variant = 'p-100',
      overflowWrap = 'normal',
      as = 'span',
      asChild,
      ...props
    },
    ref,
  ) => {
    const classes = classNames(
      scss['text'],
      scss[variant],
      {
        [scss[`overflow-wrap-${overflowWrap}`]]: overflowWrap,
      },
      className,
    );

    const Tag = asChild ? Slot : as;

    const { padding, color, width, height, display, margin, ...textProps } =
      props;

    return (
      <Layout
        padding={padding}
        display={display}
        color={color}
        width={width}
        height={height}
        margin={margin}
        asChild
      >
        <Tag {...textProps} ref={ref} className={classes}>
          {children}
        </Tag>
      </Layout>
    );
  },
);

Text.displayName = 'Text';
